import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import type { DropResult } from '@hello-pangea/dnd'
import api, { Deal, DealActivity, PaginatedResponse, PipelineSummary, formatMMK, formatMMKShort } from '@/lib/api'
import DataTable from '@/components/DataTable'
import AdvancedFilters from '@/components/AdvancedFilters'
import FilteredEmptyState from '@/components/FilteredEmptyState'
import SlideOver from '@/components/SlideOver'
import ContactSearchSelect from '@/components/ContactSearchSelect'
import type { ContactInfo } from '@/components/ContactSearchSelect'
import { Plus, Search, LayoutGrid, List, Phone, Calendar, CheckSquare, Clock, AlertCircle, UserCheck, Edit3, Upload, X, FileText, Image, Paperclip } from 'lucide-react'
import CommentsSection from '@/components/CommentsSection'
import UserSearchSelect from '@/components/UserSearchSelect'
import { ADMIN_ROLES, CRM_WRITE_ROLES, UserRole } from '@/lib/api'
import { useMyPermissions } from '@/hooks/useRolePermissions'
import AuditInfo from '@/components/AuditInfo'
import { formatDateOnly } from '@/lib/dateUtils'

function getDealFileSrc(file: any, dealId: number): string {
  if (!file) return ''
  if (file.storage === 'sharepoint' && file.sharepoint_path) {
    const token = localStorage.getItem('token')
    const base = `/api/deals/${dealId}/download?path=${encodeURIComponent(file.sharepoint_path)}`
    return token ? `${base}&token=${encodeURIComponent(token)}` : base
  }
  return file.url || ''
}

const STAGES = ['Qualification', 'Need Analysis', 'Proposal', 'Negotiation', 'Won', 'Lost']
const STAGE_COLORS: Record<string, string> = {
  'Qualification': '#3b82f6', 'Need Analysis': '#8b5cf6', 'Proposal': '#f59e0b',
  'Negotiation': '#ef4444', 'Won': '#10b981', 'Lost': '#6b7280',
}
const ACTIVITY_TYPES = ['Scheduled Call', 'Scheduled Meeting', 'Task', 'Completed Call', 'Completed Meeting']

export default function Deals() {
  const queryClient = useQueryClient()
  const [view, setView] = useState<'table' | 'kanban'>('kanban')
  const [search, setSearch] = useState('')
  const [stageFilter, setStageFilter] = useState('')
  const [ownerFilter, setOwnerFilter] = useState('')
  const [clientFilter, setClientFilter] = useState('')
  const [clientFilterName, setClientFilterName] = useState('')
  const [expectedCloseFrom, setExpectedCloseFrom] = useState('')
  const [expectedCloseTo, setExpectedCloseTo] = useState('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const [updatedFrom, setUpdatedFrom] = useState('')
  const [updatedTo, setUpdatedTo] = useState('')
  const [valueMin, setValueMin] = useState('')
  const [valueMax, setValueMax] = useState('')
  const [page, setPage] = useState(1)
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [form, setForm] = useState<any>({})
  const [activityTab, setActivityTab] = useState<'info' | 'schedule' | 'log' | 'timeline' | 'attachments'>('info')
  const [activityForm, setActivityForm] = useState<any>({})
  const [error, setError] = useState('')
  const [selectedContact, setSelectedContact] = useState('')
  const [showDealOwnerChange, setShowDealOwnerChange] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [photoError, setPhotoError] = useState('')
  const [docError, setDocError] = useState('')
  const photoInputRef = useRef<HTMLInputElement>(null)
  const docInputRef = useRef<HTMLInputElement>(null)

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  const userRole = currentUser.role as UserRole
  const isAdmin = (ADMIN_ROLES as readonly string[]).includes(userRole) || userRole === 'director'
  const isSalesManager = (userRole === 'sales_manager' || userRole === 'asst_sales_manager')
  const isFinance = userRole === 'finance'
  const { canDo } = useMyPermissions()
  const canWrite = canDo('crm_write')

  const { data: crmOwners } = useQuery<{ id: number; full_name: string; role: string }[]>({
    queryKey: ['crm-owners'],
    queryFn: () => api.get('/users/crm-owners').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })

  const advancedFilterCount = [clientFilter, expectedCloseFrom, expectedCloseTo, createdFrom, createdTo, updatedFrom, updatedTo, valueMin, valueMax].filter(Boolean).length
  const advancedParams = {
    client_id: clientFilter || undefined,
    expected_close_from: expectedCloseFrom || undefined,
    expected_close_to: expectedCloseTo || undefined,
    created_from: createdFrom || undefined,
    created_to: createdTo || undefined,
    updated_from: updatedFrom || undefined,
    updated_to: updatedTo || undefined,
    value_min: valueMin || undefined,
    value_max: valueMax || undefined,
  }
  const resetAdvancedFilters = () => {
    setClientFilter(''); setClientFilterName(''); setExpectedCloseFrom(''); setExpectedCloseTo('')
    setCreatedFrom(''); setCreatedTo(''); setUpdatedFrom(''); setUpdatedTo('')
    setValueMin(''); setValueMax(''); setPage(1)
  }
  const resetAllFilters = () => {
    setSearch(''); setStageFilter(''); setOwnerFilter(''); resetAdvancedFilters()
  }
  useEffect(() => {
    setPage(1)
  }, [clientFilter, expectedCloseFrom, expectedCloseTo, createdFrom, createdTo, updatedFrom, updatedTo, valueMin, valueMax])

  const { data: paginatedDeals, isLoading } = useQuery<PaginatedResponse<Deal>>({
    queryKey: ['deals', search, stageFilter, ownerFilter, advancedParams, page],
    queryFn: () => api.get('/deals', { params: { search, stage: stageFilter, owner: ownerFilter, ...advancedParams, page, per_page: 25 } }).then(r => r.data),
  })

  const { data: allDeals } = useQuery<Deal[]>({
    queryKey: ['deals-all', search, stageFilter, ownerFilter, advancedParams],
    queryFn: () => api.get('/deals/all', { params: { search, stage: stageFilter, owner: ownerFilter, ...advancedParams } }).then(r => r.data),
    enabled: view === 'kanban',
  })

  const { data: pipeline } = useQuery<PipelineSummary>({
    queryKey: ['pipeline-summary'],
    queryFn: () => api.get('/deals/pipeline-summary').then(r => r.data),
  })

  const { data: activities } = useQuery<DealActivity[]>({
    queryKey: ['deal-activities', selectedDeal?.id],
    queryFn: () => api.get(`/deals/${selectedDeal!.id}/activities`).then(r => r.data),
    enabled: !!selectedDeal,
  })


  const createDeal = useMutation({
    mutationFn: (data: any) => api.post('/deals', data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['deals'] }); queryClient.invalidateQueries({ queryKey: ['deals-all'] }); queryClient.invalidateQueries({ queryKey: ['pipeline-summary'] }); setShowCreate(false); setForm({}); setSelectedContact(''); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to create deal') },
  })

  const updateDeal = useMutation({
    mutationFn: ({ id, ...data }: any) => api.put(`/deals/${id}`, data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: ['deals'] }); queryClient.invalidateQueries({ queryKey: ['deals-all'] }); queryClient.invalidateQueries({ queryKey: ['pipeline-summary'] }); setSelectedDeal(res.data); setEditMode(false); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to update deal') },
  })

  const deleteDeal = useMutation({
    mutationFn: (id: number) => api.delete(`/deals/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['deals'] }); queryClient.invalidateQueries({ queryKey: ['deals-all'] }); queryClient.invalidateQueries({ queryKey: ['pipeline-summary'] }); setSelectedDeal(null); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to delete deal') },
  })

  const createActivity = useMutation({
    mutationFn: (data: any) => api.post(`/deals/${selectedDeal!.id}/activities`, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['deal-activities'] }); setActivityForm({}); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to create activity') },
  })

  const deletePhoto = useMutation({
    mutationFn: ({ dealId, photoId }: { dealId: number; photoId: string }) =>
      api.delete(`/deals/${dealId}/photos/${photoId}`),
    onSuccess: (_res, vars) => {
      setSelectedDeal(prev => prev ? { ...prev, photos: (prev.photos || []).filter((p: any) => p.id !== vars.photoId) } : prev)
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['deals-all'] })
    },
  })

  const deleteDoc = useMutation({
    mutationFn: ({ dealId, docId }: { dealId: number; docId: string }) =>
      api.delete(`/deals/${dealId}/documents/${docId}`),
    onSuccess: (_res, vars) => {
      setSelectedDeal(prev => prev ? { ...prev, documents: (prev.documents || []).filter((d: any) => d.id !== vars.docId) } : prev)
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['deals-all'] })
    },
  })

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selectedDeal) return
    setPhotoError('')
    setUploadingPhoto(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post(`/deals/${selectedDeal.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setSelectedDeal(prev => prev ? { ...prev, photos: [...(prev.photos || []), res.data.file] } : prev)
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['deals-all'] })
    } catch (err: any) {
      setPhotoError(err.response?.data?.detail || 'Failed to upload photo')
    } finally {
      setUploadingPhoto(false)
      if (photoInputRef.current) photoInputRef.current.value = ''
    }
  }

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selectedDeal) return
    setDocError('')
    setUploadingDoc(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post(`/deals/${selectedDeal.id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setSelectedDeal(prev => prev ? { ...prev, documents: [...(prev.documents || []), res.data.file] } : prev)
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['deals-all'] })
    } catch (err: any) {
      setDocError(err.response?.data?.detail || 'Failed to upload document')
    } finally {
      setUploadingDoc(false)
      if (docInputRef.current) docInputRef.current.value = ''
    }
  }

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return
    const dealId = parseInt(result.draggableId)
    const newStage = result.destination.droppableId
    updateDeal.mutate({ id: dealId, stage: newStage })
  }

  const columns = [
    { key: 'deal_name', label: 'Deal', render: (d: Deal) => <span className="font-medium text-slate-900">{d.deal_name}</span> },
    { key: 'client', label: 'Client', render: (d: Deal) => d.clients?.client_name || '—' },
    { key: 'stage', label: 'Stage', render: (d: Deal) => (
      <span className="text-xs px-2 py-0.5 rounded-full font-medium text-white" style={{ backgroundColor: STAGE_COLORS[d.stage] }}>{d.stage}</span>
    )},
    { key: 'value', label: 'Value', render: (d: Deal) => <span className="font-medium">{formatMMK(Number(d.value))}</span> },
    { key: 'owner', label: 'Owner' },
    { key: 'expected_close', label: 'Close Date', render: (d: Deal) => d.expected_close || '—' },
  ]

  const kanbanDeals = allDeals || []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Deals</h1>
        <div className="flex gap-2">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setView('kanban')} className={`px-3 py-1.5 text-sm rounded-md transition-colors min-h-[36px] ${view === 'kanban' ? 'bg-white shadow-sm font-medium' : 'text-slate-500'}`}><LayoutGrid size={16} /></button>
            <button onClick={() => setView('table')} className={`px-3 py-1.5 text-sm rounded-md transition-colors min-h-[36px] ${view === 'table' ? 'bg-white shadow-sm font-medium' : 'text-slate-500'}`}><List size={16} /></button>
          </div>
          {canWrite && (
            <button onClick={() => { setShowCreate(true); setForm({ stage: 'Qualification', value: 0, owner: currentUser.full_name || '' }); setError('') }}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900 min-h-[44px]">
              <Plus size={16} /> New Deal
            </button>
          )}
        </div>
      </div>

      {pipeline && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5 sm:gap-2">
          {STAGES.map(s => (
            <div key={s} className="bg-white rounded-lg p-3 border border-slate-200 text-center">
              <div className="text-lg font-bold" style={{ color: STAGE_COLORS[s] }}>{pipeline[s]?.count || 0}</div>
              <div className="text-xs text-slate-500">{s}</div>
              <div className="text-xs font-medium text-slate-700">{formatMMKShort(pipeline[s]?.value || 0)}</div>
            </div>
          ))}
        </div>
      )}

      {view === 'table' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-3 border-b border-slate-200 flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input type="text" placeholder="Search deals..." value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
            </div>
            <select value={stageFilter} onChange={e => { setStageFilter(e.target.value); setPage(1) }}
              className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
              <option value="">All Stages</option>
              {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {crmOwners && crmOwners.length > 1 && (
              <select value={ownerFilter} onChange={e => { setOwnerFilter(e.target.value); setPage(1) }}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
                <option value="">All Owners</option>
                {crmOwners.map(o => <option key={o.id} value={o.full_name}>{o.full_name}</option>)}
              </select>
            )}
            <AdvancedFilters activeCount={advancedFilterCount} onReset={resetAdvancedFilters} onApply={() => setPage(1)}>
              <ContactSearchSelect
                value={clientFilterName}
                label="Client"
                onSelect={client => { setClientFilter(String(client.id)); setClientFilterName(client.client_name) }}
                onClear={() => { setClientFilter(''); setClientFilterName('') }}
              />
              <label className="text-xs font-medium text-slate-600">Expected close from
                <input type="date" value={expectedCloseFrom} onChange={e => setExpectedCloseFrom(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
              </label>
              <label className="text-xs font-medium text-slate-600">Expected close to
                <input type="date" value={expectedCloseTo} onChange={e => setExpectedCloseTo(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
              </label>
              <label className="text-xs font-medium text-slate-600">Created from
                <input type="date" value={createdFrom} onChange={e => setCreatedFrom(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
              </label>
              <label className="text-xs font-medium text-slate-600">Created to
                <input type="date" value={createdTo} onChange={e => setCreatedTo(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
              </label>
              <label className="text-xs font-medium text-slate-600">Updated from
                <input type="date" value={updatedFrom} onChange={e => setUpdatedFrom(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
              </label>
              <label className="text-xs font-medium text-slate-600">Updated to
                <input type="date" value={updatedTo} onChange={e => setUpdatedTo(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
              </label>
              <label className="text-xs font-medium text-slate-600">Minimum value
                <input type="number" min="0" value={valueMin} onChange={e => setValueMin(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" placeholder="MMK" />
              </label>
              <label className="text-xs font-medium text-slate-600">Maximum value
                <input type="number" min="0" value={valueMax} onChange={e => setValueMax(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" placeholder="MMK" />
              </label>
            </AdvancedFilters>
          </div>
          <DataTable columns={columns} data={paginatedDeals?.data || []} total={paginatedDeals?.total || 0}
            page={page} totalPages={paginatedDeals?.total_pages || 1} onPageChange={setPage}
            onRowClick={d => { setSelectedDeal(d); setActivityTab('info'); setEditMode(false) }} selectedId={selectedDeal?.id}
            getRowId={d => d.id} loading={isLoading} />
          <FilteredEmptyState show={!isLoading && (paginatedDeals?.total || 0) === 0 && !!(search || stageFilter || ownerFilter || advancedFilterCount)} onReset={resetAllFilters} />
        </div>
      )}

      {view === 'kanban' && (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 400 }}>
            {STAGES.map(stage => {
              const stageDeals = kanbanDeals.filter(d => d.stage === stage)
              return (
                <Droppable key={stage} droppableId={stage}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}
                      className={`flex-shrink-0 w-64 bg-slate-100 rounded-xl p-2 transition-colors ${snapshot.isDraggingOver ? 'bg-red-50' : ''}`}>
                      <div className="flex items-center gap-2 px-2 py-2 mb-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STAGE_COLORS[stage] }} />
                        <span className="text-sm font-semibold text-slate-700">{stage}</span>
                        <span className="text-xs bg-white px-1.5 py-0.5 rounded-full text-slate-500 ml-auto">{stageDeals.length}</span>
                      </div>
                      <div className="space-y-2 min-h-[50px]">
                        {stageDeals.map((deal, idx) => (
                          <Draggable key={deal.id} draggableId={String(deal.id)} index={idx}>
                            {(provided, snapshot) => (
                              <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                onClick={() => { setSelectedDeal(deal); setActivityTab('info'); setEditMode(false) }}
                                className={`bg-white rounded-lg p-3 border border-slate-200 cursor-pointer hover:shadow-md transition-shadow ${snapshot.isDragging ? 'shadow-lg rotate-2' : ''}`}
                                style={{ ...provided.draggableProps.style, borderLeft: `3px solid ${STAGE_COLORS[stage]}` }}>
                                <div className="font-medium text-sm text-slate-900 mb-1">{deal.deal_name}</div>
                                <div className="text-xs text-slate-500 mb-2">{deal.clients?.client_name || 'No client'}</div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-semibold" style={{ color: STAGE_COLORS[stage] }}>{formatMMK(Number(deal.value))}</span>
                                  <span className="text-xs text-slate-400">{deal.owner || ''}</span>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                      </div>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )
            })}
          </div>
        </DragDropContext>
      )}

      <SlideOver open={!!selectedDeal} onClose={() => { setSelectedDeal(null); setEditMode(false); setShowDealOwnerChange(false); setError(''); setPhotoError(''); setDocError('') }}
        title={selectedDeal?.deal_name || ''} wide>
        {selectedDeal && (
          <div className="space-y-4">
            {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}
            <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
              {(['info', 'schedule', 'log', 'timeline', 'attachments'] as const).map(t => (
                <button key={t} onClick={() => setActivityTab(t)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activityTab === t ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500'}`}>
                  {t === 'info' ? 'Details' : t === 'schedule' ? 'Schedule' : t === 'log' ? 'Log Activity' : t === 'timeline' ? 'Timeline' : 'Attachments'}
                </button>
              ))}
            </div>

            {activityTab === 'info' && !editMode && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['Stage', selectedDeal.stage],
                    ['Value', formatMMK(Number(selectedDeal.value))],
                    ['Client', selectedDeal.clients?.client_name || '—'],
                    ['Close Date', formatDateOnly(selectedDeal.expected_close)],
                  ].map(([l, v]) => (
                    <div key={l as string} className="text-sm">
                      <span className="text-slate-500 block text-xs">{l}</span>
                      <span className="text-slate-900 font-medium">{v}</span>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center text-sm border-t border-slate-100 pt-3">
                  <span className="text-slate-500">Owner</span>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-900 font-medium">{selectedDeal.owner || '—'}</span>
                    {canWrite && (isAdmin || isSalesManager || currentUser.full_name === selectedDeal.owner) && !showDealOwnerChange && (
                      <button onClick={() => setShowDealOwnerChange(true)}
                        className="text-xs text-red-800 hover:text-red-900 font-medium">Change</button>
                    )}
                  </div>
                </div>

                {showDealOwnerChange && (
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                    <UserSearchSelect
                      label="New Owner"
                      value=""
                      onChange={(name) => {
                        if (name) {
                          updateDeal.mutate({ id: selectedDeal.id, owner: name })
                          setShowDealOwnerChange(false)
                        }
                      }}
                      placeholder="Search for new owner..."
                    />
                    <button type="button" onClick={() => setShowDealOwnerChange(false)}
                      className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                  </div>
                )}

                {selectedDeal.notes && <div className="text-sm"><span className="text-slate-500 text-xs block">Notes</span><p className="text-slate-700">{selectedDeal.notes}</p></div>}

                <AuditInfo
                  createdByName={selectedDeal.created_by_name}
                  createdAt={selectedDeal.created_at}
                  updatedAt={selectedDeal.updated_at}
                />

                <CommentsSection entityType="deal" entityId={selectedDeal.id} />

                {activities && activities.filter(a => a.activity_type.startsWith('System:')).length > 0 && (
                  <div className="border-t border-slate-200 pt-3">
                    <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Audit Trail</div>
                    <div className="space-y-2">
                      {activities.filter(a => a.activity_type.startsWith('System:')).map(a => (
                        <div key={a.id} className="flex gap-2 p-2 bg-slate-50 rounded-lg text-xs">
                          <div className={`mt-0.5 p-1 rounded ${a.activity_type.includes('Created') ? 'bg-green-100 text-green-600' : a.activity_type.includes('Owner') ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>
                            {a.activity_type.includes('Created') ? <Plus size={12} /> : a.activity_type.includes('Owner') ? <UserCheck size={12} /> : <Edit3 size={12} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-slate-900 font-medium">{a.subject}</div>
                            <div className="text-slate-400">{a.description}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {canWrite && (isAdmin || isSalesManager || currentUser.full_name === selectedDeal.owner) && (
                  <div className="flex gap-2 pt-3 border-t">
                    <button onClick={() => { setEditMode(true); setForm(selectedDeal); setSelectedContact(selectedDeal.clients ? (selectedDeal.clients.company ? `${selectedDeal.clients.client_name} — ${selectedDeal.clients.company}` : selectedDeal.clients.client_name) : '') }} className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Edit</button>
                    <button onClick={() => { if (confirm('Delete deal?')) deleteDeal.mutate(selectedDeal.id) }}
                      className="py-2 px-4 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100">Delete</button>
                  </div>
                )}
              </div>
            )}

            {activityTab === 'info' && editMode && (
              <form onSubmit={e => { e.preventDefault(); updateDeal.mutate({ id: selectedDeal.id, deal_name: form.deal_name, client_id: form.client_id, stage: form.stage, value: parseFloat(form.value) || 0, owner: form.owner, expected_close: form.expected_close || null, notes: form.notes }) }} className="space-y-3">
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Deal Name</label>
                  <input value={form.deal_name || ''} onChange={e => setForm({ ...form, deal_name: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Client</label>
                  <ContactSearchSelect
                    value={selectedContact}
                    onSelect={(c: ContactInfo) => { setForm({ ...form, client_id: c.id }); setSelectedContact(c.company ? `${c.client_name} — ${c.company}` : c.client_name) }}
                    onClear={() => { setForm({ ...form, client_id: null }); setSelectedContact('') }}
                  />
                </div>
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Stage</label>
                  <select value={form.stage || ''} onChange={e => setForm({ ...form, stage: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
                    {STAGES.map(s => <option key={s}>{s}</option>)}
                  </select></div>
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Value</label>
                  <input type="number" value={form.value || ''} onChange={e => setForm({ ...form, value: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
                <UserSearchSelect
                  label="Owner"
                  value={form.owner || ''}
                  onChange={(name) => setForm({ ...form, owner: name })}
                  placeholder="Search for owner..."
                />
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Close Date</label>
                  <input type="date" value={form.expected_close || ''} onChange={e => setForm({ ...form, expected_close: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Notes</label>
                  <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
                <div className="flex gap-2">
                  <button type="submit" className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Save</button>
                  <button type="button" onClick={() => setEditMode(false)} className="py-2 px-4 text-sm bg-slate-100 rounded-lg">Cancel</button>
                </div>
              </form>
            )}

            {activityTab === 'schedule' && (
              <form onSubmit={e => { e.preventDefault(); createActivity.mutate(activityForm); setActivityForm({}) }} className="space-y-3">
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Type</label>
                  <select value={activityForm.activity_type || ''} onChange={e => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" required>
                    <option value="">Select...</option>
                    <option>Scheduled Call</option><option>Scheduled Meeting</option><option>Task</option>
                  </select></div>
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Subject</label>
                  <input value={activityForm.subject || ''} onChange={e => setActivityForm({ ...activityForm, subject: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" required /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-sm font-medium text-slate-700 block mb-1">Date</label>
                    <input type="date" value={activityForm.activity_date || ''} onChange={e => setActivityForm({ ...activityForm, activity_date: e.target.value })}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
                  <div><label className="text-sm font-medium text-slate-700 block mb-1">Time</label>
                    <input type="time" value={activityForm.activity_time || ''} onChange={e => setActivityForm({ ...activityForm, activity_time: e.target.value })}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
                </div>
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Description</label>
                  <textarea value={activityForm.description || ''} onChange={e => setActivityForm({ ...activityForm, description: e.target.value })} rows={2}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
                <button type="submit" disabled={createActivity.isPending}
                  className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">Schedule Activity</button>
              </form>
            )}

            {activityTab === 'log' && (
              <form onSubmit={e => { e.preventDefault(); createActivity.mutate({ ...activityForm, status: 'Completed' }); setActivityForm({}) }} className="space-y-3">
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Type</label>
                  <select value={activityForm.activity_type || ''} onChange={e => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" required>
                    <option value="">Select...</option>
                    <option>Completed Call</option><option>Completed Meeting</option>
                  </select></div>
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Subject</label>
                  <input value={activityForm.subject || ''} onChange={e => setActivityForm({ ...activityForm, subject: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" required /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-sm font-medium text-slate-700 block mb-1">Date</label>
                    <input type="date" value={activityForm.activity_date || ''} onChange={e => setActivityForm({ ...activityForm, activity_date: e.target.value })}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
                  <div><label className="text-sm font-medium text-slate-700 block mb-1">Time</label>
                    <input type="time" value={activityForm.activity_time || ''} onChange={e => setActivityForm({ ...activityForm, activity_time: e.target.value })}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
                </div>
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Result/Outcome</label>
                  <textarea value={activityForm.result || ''} onChange={e => setActivityForm({ ...activityForm, result: e.target.value })} rows={3}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
                <button type="submit" disabled={createActivity.isPending}
                  className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">Log Activity</button>
              </form>
            )}

            {activityTab === 'timeline' && (
              <div className="space-y-3">
                {(!activities || activities.length === 0) ? (
                  <p className="text-sm text-slate-500 text-center py-4">No activities yet</p>
                ) : activities.map(a => (
                  a.activity_type.startsWith('System:') ? (
                    <div key={a.id} className="flex gap-2 p-2 bg-slate-50 rounded-lg text-xs">
                      <div className={`mt-0.5 p-1 rounded ${a.activity_type.includes('Created') ? 'bg-green-100 text-green-600' : a.activity_type.includes('Owner') ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>
                        {a.activity_type.includes('Created') ? <Plus size={12} /> : a.activity_type.includes('Owner') ? <UserCheck size={12} /> : <Edit3 size={12} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-900 font-medium">{a.subject}</div>
                        <div className="text-slate-400">{a.description}</div>
                      </div>
                    </div>
                  ) : (
                  <div key={a.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className={`mt-0.5 p-1.5 rounded-lg ${a.activity_type.includes('Call') ? 'bg-red-100 text-red-800' : a.activity_type.includes('Meeting') ? 'bg-purple-100 text-purple-600' : 'bg-amber-100 text-amber-600'}`}>
                      {a.activity_type.includes('Call') ? <Phone size={14} /> : a.activity_type.includes('Meeting') ? <Calendar size={14} /> : <CheckSquare size={14} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium text-slate-900">{a.subject}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${a.status === 'Completed' ? 'bg-green-100 text-green-700' : a.status === 'Cancelled' ? 'bg-red-100 text-red-700' : 'bg-red-100 text-red-900'}`}>{a.status}</span>
                      </div>
                      <div className="text-xs text-slate-500">{a.activity_type} {a.activity_date ? `on ${a.activity_date}` : ''} {a.activity_time || ''}</div>
                      {a.result && <div className="text-sm text-slate-700 mt-1 bg-white p-2 rounded border border-slate-200">{a.result}</div>}
                    </div>
                  </div>
                  )
                ))}
              </div>
            )}

            {activityTab === 'attachments' && (() => {
              const photos: any[] = selectedDeal.photos || []
              const documents: any[] = selectedDeal.documents || []
              const canUpload = canWrite && (isAdmin || isSalesManager || currentUser.full_name === selectedDeal.owner)
              return (
                <div className="space-y-4">
                  {/* Photos */}
                  <div className="border border-slate-200 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                        <Image size={12} /> Photos ({photos.length})
                      </p>
                      {canUpload && (
                        <label className={`flex items-center gap-1.5 px-3 py-1 text-xs bg-slate-700 text-white rounded-lg cursor-pointer hover:bg-slate-800 ${uploadingPhoto ? 'opacity-60 cursor-not-allowed' : ''}`}>
                          <Upload size={12} /> {uploadingPhoto ? 'Uploading...' : 'Upload'}
                          <input ref={photoInputRef} type="file" accept="image/jpeg,image/jpg,image/png" className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                        </label>
                      )}
                    </div>
                    {photoError && <p className="text-xs text-red-600 mb-2">{photoError}</p>}
                    {photos.length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-3">No photos uploaded yet</p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {photos.map((p: any) => (
                          <div key={p.id} className="relative group aspect-square">
                            <img src={getDealFileSrc(p, selectedDeal.id)} alt={p.name} className="w-full h-full object-cover rounded-lg border border-slate-200" />
                            {canUpload && (
                              <button
                                onClick={() => { if (confirm('Delete this photo?')) deletePhoto.mutate({ dealId: selectedDeal.id, photoId: p.id }) }}
                                className="absolute top-1 right-1 p-0.5 bg-red-600 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                <X size={10} />
                              </button>
                            )}
                            <p className="text-xs text-slate-500 truncate mt-0.5">{p.name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Documents */}
                  <div className="border border-slate-200 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                        <Paperclip size={12} /> Documents ({documents.length})
                      </p>
                      {canUpload && (
                        <label className={`flex items-center gap-1.5 px-3 py-1 text-xs bg-slate-700 text-white rounded-lg cursor-pointer hover:bg-slate-800 ${uploadingDoc ? 'opacity-60 cursor-not-allowed' : ''}`}>
                          <Upload size={12} /> {uploadingDoc ? 'Uploading...' : 'Upload'}
                          <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*" className="hidden" onChange={handleDocUpload} disabled={uploadingDoc} />
                        </label>
                      )}
                    </div>
                    {docError && <p className="text-xs text-red-600 mb-2">{docError}</p>}
                    {documents.length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-3">No documents uploaded yet</p>
                    ) : (
                      <div className="space-y-2">
                        {documents.map((d: any) => (
                          <div key={d.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                            <FileText size={14} className="text-slate-500 shrink-0" />
                            <a href={getDealFileSrc(d, selectedDeal.id)} target="_blank" rel="noopener noreferrer"
                              className="flex-1 text-xs text-red-800 underline truncate">{d.name}</a>
                            <span className="text-xs text-slate-400 shrink-0">{d.size ? `${(d.size / 1024).toFixed(0)}KB` : ''}</span>
                            {canUpload && (
                              <button onClick={() => { if (confirm('Delete this document?')) deleteDoc.mutate({ dealId: selectedDeal.id, docId: d.id }) }}
                                className="p-0.5 text-red-500 hover:text-red-700 shrink-0">
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )
            })()}
          </div>
        )}
      </SlideOver>

      <SlideOver open={showCreate} onClose={() => { setShowCreate(false); setSelectedContact(''); setError('') }} title="New Deal">
        <form onSubmit={e => {
          e.preventDefault()
          if (!form.client_id) { setError('Please create a contact first before creating a deal.'); return }
          createDeal.mutate(form)
        }} className="space-y-3">
          {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Deal Name *</label>
            <input value={form.deal_name || ''} onChange={e => setForm({ ...form, deal_name: e.target.value })}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" required /></div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Contact <span className="text-red-600">*</span></label>
            <ContactSearchSelect
              value={selectedContact}
              onSelect={(c: ContactInfo) => { setForm({ ...form, client_id: c.id }); setSelectedContact(c.company ? `${c.client_name} — ${c.company}` : c.client_name); setError('') }}
              onClear={() => { setForm({ ...form, client_id: null }); setSelectedContact('') }}
            />
            {!form.client_id && <p className="text-xs text-slate-500 mt-1">A contact must be linked to this deal. <a href="/contacts" className="text-red-700 underline">Create a contact</a> first if needed.</p>}
          </div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Stage</label>
            <select value={form.stage || 'Qualification'} onChange={e => setForm({ ...form, stage: e.target.value })}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
              {STAGES.map(s => <option key={s}>{s}</option>)}
            </select></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Value</label>
            <input type="number" value={form.value || ''} onChange={e => setForm({ ...form, value: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
          <UserSearchSelect
            label="Owner"
            value={form.owner || ''}
            onChange={(name) => setForm({ ...form, owner: name })}
            placeholder="Search for owner..."
          />
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Expected Close Date</label>
            <input type="date" value={form.expected_close || ''} onChange={e => setForm({ ...form, expected_close: e.target.value })}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Notes</label>
            <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
          <button type="submit" disabled={createDeal.isPending}
            className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
            {createDeal.isPending ? 'Creating...' : 'Create Deal'}
          </button>
        </form>
      </SlideOver>
    </div>
  )
}
